import { ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface GuardrailEntryData {
  title: string;
  description: string;
  category: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  pattern: string;
  fix: string;
  fitnessTest: string;
  /** Logical IDs of the decisions/requirements this check enforces
   *  (Documentation Standard §3.4/§5.4) — e.g. docs-acme#ADR-016.
   *  The API returns a list; the edit form round-trips a comma string. */
  cites?: string[] | string;
}

/** Display form of cites for both the view card and the edit input. */
function citesText(cites?: string[] | string): string {
  return Array.isArray(cites) ? cites.join(', ') : (cites ?? '');
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'text-red-600 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  high: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
  medium: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  low: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
};

interface EditableGuardrailEntryProps {
  entry: GuardrailEntryData;
  editing?: boolean;
  /** Called when a field value changes. If omitted, edit mode is not available. */
  onUpdate?: (field: string, value: string) => void;
  /** Compact mode shows only title + severity inline (used in InsightsPage list view). */
  compact?: boolean;
}

export function EditableGuardrailEntry({
  entry,
  editing = false,
  onUpdate,
  compact = false,
}: EditableGuardrailEntryProps) {
  if (editing && onUpdate) {
    return <EditForm entry={entry} onUpdate={onUpdate} />;
  }

  if (compact) {
    return (
      <div className="text-xs text-slate-700 dark:text-slate-300 flex items-center gap-2">
        <ShieldAlert className="w-3 h-3 text-rose-500 shrink-0" />
        <span className="font-medium">{entry.title}</span>
        <span className="text-slate-400">({entry.severity})</span>
      </div>
    );
  }

  return <ViewCard entry={entry} />;
}

function EditForm({
  entry,
  onUpdate,
}: {
  entry: GuardrailEntryData;
  onUpdate: (field: string, value: string) => void;
}) {
  return (
    <div className="space-y-2 p-3 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
      <div className="grid grid-cols-2 gap-2">
        <input
          value={entry.title}
          onChange={(e) => onUpdate('title', e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          placeholder="Title"
        />
        <select
          value={entry.severity}
          onChange={(e) => onUpdate('severity', e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
        >
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>
      <textarea
        value={entry.description}
        onChange={(e) => onUpdate('description', e.target.value)}
        className="w-full text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 resize-y"
        rows={2}
        placeholder="Description"
      />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-medium text-slate-500 uppercase">Bad Pattern</label>
          <textarea
            value={entry.pattern}
            onChange={(e) => onUpdate('pattern', e.target.value)}
            className="w-full text-xs px-2 py-1.5 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-slate-900 dark:text-slate-100 font-mono resize-y"
            rows={2}
          />
        </div>
        <div>
          <label className="text-[10px] font-medium text-slate-500 uppercase">Correct Approach</label>
          <textarea
            value={entry.fix}
            onChange={(e) => onUpdate('fix', e.target.value)}
            className="w-full text-xs px-2 py-1.5 rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-slate-900 dark:text-slate-100 font-mono resize-y"
            rows={2}
          />
        </div>
      </div>
      <div>
        <label className="text-[10px] font-medium text-slate-500 uppercase">
          Enforces (ADR / requirement IDs)
        </label>
        <input
          value={citesText(entry.cites)}
          onChange={(e) => onUpdate('cites', e.target.value)}
          className="w-full text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-mono"
          placeholder="docs-acme#ADR-016, product#req/rate-limiting"
        />
      </div>
      <div>
        <label className="text-[10px] font-medium text-slate-500 uppercase">Fitness Test</label>
        <textarea
          value={entry.fitnessTest}
          onChange={(e) => onUpdate('fitnessTest', e.target.value)}
          className="w-full text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono resize-y"
          rows={2}
        />
      </div>
    </div>
  );
}

function ViewCard({ entry }: { entry: GuardrailEntryData }) {
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-[10px] px-1.5 py-0.5 rounded font-medium border',
                SEVERITY_COLORS[entry.severity],
              )}
            >
              {entry.severity}
            </span>
            <span className="text-xs text-slate-500">{entry.category}</span>
          </div>
          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 mt-1">
            {entry.title}
          </h4>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
            {entry.description}
          </p>
          {citesText(entry.cites) && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
              Enforces{' '}
              <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">
                {citesText(entry.cites)}
              </code>
            </p>
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-medium text-slate-500 uppercase mb-1">Bad Pattern</p>
          <code className="block text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded px-2 py-1.5 whitespace-pre-wrap">
            {entry.pattern}
          </code>
        </div>
        <div>
          <p className="text-[10px] font-medium text-slate-500 uppercase mb-1">Correct Approach</p>
          <code className="block text-xs bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 rounded px-2 py-1.5 whitespace-pre-wrap">
            {entry.fix}
          </code>
        </div>
      </div>
      <div className="mt-3">
        <p className="text-[10px] font-medium text-slate-500 uppercase mb-1">
          Architecture Fitness Test
        </p>
        <code className="block text-xs bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded px-2 py-1.5 whitespace-pre-wrap font-mono">
          {entry.fitnessTest}
        </code>
      </div>
    </div>
  );
}
